/**
 * Copyright (c) 2026, Ouster, Inc.
 * All rights reserved.
 */

/**
 * Formatting for the Ouster WASM SDK's stderr stream (its `printErr` hook).
 *
 * The SDK is C++ compiled to WASM and does its own logging, prefixing each
 * line with a timestamp, a source and a severity, e.g.
 *
 *   [2026-09-09 16:26:29.847] [ouster::sdk::core] [warning] Deserializing
 *   object [id: 4] object_to_body with invalid pose position
 *
 * Two problems this solves. First, every call site used to funnel the whole
 * stream through console.error, so lines the SDK itself labels [warning] or
 * [info] surfaced as JS errors. Second, the SDK emits some diagnostics once
 * per object - a recording with damaged pose data produces one identical-in-
 * substance line per id, which buries everything else in the console.
 *
 * The SDK exposes no log-level control to JS (there is no set_log_level on the
 * module or in its typings), so filtering has to happen here.
 *
 * NOTE: this file is deliberately dependency-free. It is used from Web
 * Workers, which must not pull in the React component library.
 * ouster-viewer carries an identical copy; keep the two in sync. Nothing
 * enforces that - see docs/TODO-shared-pure-utils.md for why the copies
 * exist and what it would take to remove them.
 */

const SEVERITY_PATTERN = /\[(critical|error|warn(?:ing)?|info|debug|trace)\]/i;

// Repeats are summarised only when the count reaches one of these, so a
// runaway diagnostic costs a handful of lines instead of thousands.
const REPEAT_MILESTONES = new Set([10, 100, 1_000, 10_000]);

const repeatCounts = new Map<string, number>();

/**
 * Collapse the parts of a line that vary between repeats so per-object
 * diagnostics share a single key - the lines above differ only by their
 * timestamp and object id.
 */
const dedupeKey = (line: string): string =>
  line.replace(/^\s*\[[^\]]*\]\s*/, '').replace(/\d+/g, 'N');

const consoleForSeverity = (severity?: string): ((message: string) => void) => {
  switch (severity) {
    case 'critical':
    case 'error':
      return (message) => console.error(message);
    case 'warn':
    case 'warning':
      return (message) => console.warn(message);
    case 'info':
      return (message) => console.info(message);
    case 'debug':
    case 'trace':
      return (message) => console.debug(message);
    default:
      // Untagged lines stay errors on purpose: Emscripten also routes real
      // runtime failures and abort messages through printErr, and those must
      // not be quietly downgraded just because they carry no severity tag.
      return (message) => console.error(message);
  }
};

/**
 * Build a `printErr` handler that logs at the SDK's own severity and
 * suppresses floods of the same diagnostic.
 *
 * @param prefix - tag identifying the SDK instance, e.g. '[worker:sdk]'
 * @returns a handler suitable for loadOusterSDK's printErr option
 */
export const createSdkStderrLogger =
  (prefix: string) =>
  (...args: unknown[]): void => {
    const line = args.map((arg) => String(arg)).join(' ');
    const severity = SEVERITY_PATTERN.exec(line)?.[1]?.toLowerCase();
    const log = consoleForSeverity(severity);

    const key = dedupeKey(line);
    const count = (repeatCounts.get(key) ?? 0) + 1;
    repeatCounts.set(key, count);

    if (count === 1) {
      log(`${prefix} ${line}`);
      return;
    }
    if (REPEAT_MILESTONES.has(count)) {
      log(`${prefix} ${line} (repeated ${count} times)`);
    }
  };

/** Clear the repeat bookkeeping. Intended for tests. */
export const resetSdkStderrLogger = (): void => {
  repeatCounts.clear();
};
